import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Box, Button, Chip, CircularProgress, Alert,
  TextField, Grid, Card, CardMedia, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, LinearProgress
} from '@mui/material';
import { ArrowBack, PhotoCamera, Add, Check, Close, Delete as DeleteIcon, CloudUpload } from '@mui/icons-material';

const API_URL = window.API_URL || 'http://localhost:3992/api';

export default function VistoriaMobile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const [aluguel, setAluguel] = useState(null);
  const [vistoria, setVistoria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tipoVistoria, setTipoVistoria] = useState('entrega'); // entrega ou devolucao
  const [formData, setFormData] = useState({
    km_final: '',
    nivel_combustivel: '',
    observacoes: ''
  });
  const [criando, setCriando] = useState(false);

  // Fotos
  const [fotos, setFotos] = useState([]);
  const [cameraAtiva, setCameraAtiva] = useState(false);
  const [fotoAtual, setFotoAtual] = useState(null);
  const [dialogFotoOpen, setDialogFotoOpen] = useState(false);
  const [obsAtualFoto, setObsAtualFoto] = useState('');
  const [tipoFotoAtual, setTipoFotoAtual] = useState('geral');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadAndando, setUploadAndando] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    carregarDados();
  }, [id]);

  useEffect(() => {
    if (cameraAtiva && videoRef.current && !videoRef.current.srcObject) {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        .then(stream => {
          videoRef.current.srcObject = stream;
        })
        .catch(err => {
          console.error('Erro ao acessar câmera:', err);
          setError('Câmera não disponível');
          setCameraAtiva(false);
        });
    }

    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraAtiva]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const resAluguel = await fetch(`${API_URL}/alugueis/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!resAluguel.ok) throw new Error('Aluguel não encontrado');
      const aluguelData = await resAluguel.json();
      setAluguel(aluguelData);

      try {
        const resVistoria = await fetch(`${API_URL}/vistorias/alugueis-ativos`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (resVistoria.ok) {
          const vistoriaData = await resVistoria.json();
          const vistoriaAluguel = vistoriaData.find(v => v.id_aluguel === parseInt(id));
          if (vistoriaAluguel?.vistoria_id) {
            carregarVistoria(vistoriaAluguel.vistoria_id);
          }
        }
      } catch (err) {
        console.log('Sem vistoria ainda');
      }

      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const carregarVistoria = async (vistoriaId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/${vistoriaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const vistoriaData = await res.json();
        setVistoria(vistoriaData);
        setFotos(vistoriaData.fotos || []);
      }
    } catch (err) {
      console.log('Erro ao carregar vistoria:', err.message);
    }
  };

  const criarVistoria = async () => {
    try {
      setCriando(true);
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/vistorias`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id_aluguel: parseInt(id),
          km_final: parseInt(formData.km_final),
          nivel_combustivel: parseInt(formData.nivel_combustivel),
          observacoes: formData.observacoes,
          funcionario_nome: localStorage.getItem('usuario_nome') || 'Funcionário',
          tipo_vistoria: tipoVistoria
        })
      });

      if (!res.ok) throw new Error('Erro ao criar');
      const vistoriaData = await res.json();

      setVistoria({ id: vistoriaData.vistoria_id, fotos: [], checklist: [] });
      setFotos([]);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCriando(false);
    }
  };

  const capturarFoto = () => {
    if (!canvasRef.current || !videoRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    const video = videoRef.current;

    canvasRef.current.width = video.videoWidth;
    canvasRef.current.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    canvasRef.current.toBlob(blob => {
      setFotoAtual(blob);
      setCameraAtiva(false);
      setDialogFotoOpen(true);
    }, 'image/jpeg', 0.9);
  };

  const handleArquivoSelecionado = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFotoAtual(file);
      setDialogFotoOpen(true);
    }
  };

  const salvarFoto = async () => {
    if (!fotoAtual || !vistoria) return;

    try {
      setUploadAndando(true);
      setUploadProgress(0);

      const formDataFoto = new FormData();
      formDataFoto.append('foto', fotoAtual);
      formDataFoto.append('tipo_foto', tipoFotoAtual);
      formDataFoto.append('descricao', obsAtualFoto);

      const token = localStorage.getItem('token');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 201) {
          const fotoData = JSON.parse(xhr.responseText);
          setFotos([...fotos, fotoData.foto]);
          setFotoAtual(null);
          setObsAtualFoto('');
          setTipoFotoAtual('geral');
          setDialogFotoOpen(false);
          setUploadProgress(0);
          setUploadAndando(false);
          setError('');
        } else {
          throw new Error('Erro ao enviar');
        }
      });

      xhr.addEventListener('error', () => {
        setError('Erro de conexão');
        setUploadAndando(false);
        setUploadProgress(0);
      });

      xhr.open('POST', `${API_URL}/vistorias/${vistoria.id}/upload-foto`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formDataFoto);
    } catch (err) {
      setError('Erro: ' + err.message);
      setUploadAndando(false);
    }
  };

  const deletarFoto = async (fotoId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/${vistoria.id}/fotos/${fotoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setFotos(fotos.filter(f => f.id !== fotoId));
      }
    } catch (err) {
      setError('Erro ao deletar foto');
    }
  };

  const finalizarVistoria = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/${vistoria.id}/finalizar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erro ao finalizar');
      navigate('/alugueis');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!aluguel) return <Alert severity="error">{error || 'Aluguel não encontrado'}</Alert>;

  return (
    <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/alugueis')} size="small" sx={{ mb: 2 }}>
        Voltar
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: '12px' }}>{error}</Alert>}

      {/* DADOS ALUGUEL */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9', borderLeft: '4px solid #FF8C00' }}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 'bold' }}>
          {aluguel.nome_cliente?.substring(0, 30)}
        </h3>
        <p style={{ margin: '0 0 4px 0', fontSize: '13px' }}>
          {aluguel.marca} {aluguel.modelo} - <strong>{aluguel.placa}</strong>
        </p>
        <p style={{ margin: 0, fontSize: '11px', color: '#666' }}>
          Até: {new Date(aluguel.data_devolucao).toLocaleDateString('pt-BR')}
        </p>
      </Paper>

      {!vistoria ? (
        /* CRIAR VISTORIA - TIPO */
        <Paper sx={{ p: 2, mb: 2 }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Tipo de Vistoria</h4>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={tipoVistoria === 'entrega' ? 'contained' : 'outlined'}
              onClick={() => setTipoVistoria('entrega')}
              size="small"
              fullWidth
            >
              Entrega
            </Button>
            <Button
              variant={tipoVistoria === 'devolucao' ? 'contained' : 'outlined'}
              onClick={() => setTipoVistoria('devolucao')}
              size="small"
              fullWidth
            >
              Devolução
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <TextField
            label="KM Final"
            type="number"
            value={formData.km_final}
            onChange={(e) => setFormData({ ...formData, km_final: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <TextField
            label="Combustível %"
            type="number"
            value={formData.nivel_combustivel}
            onChange={(e) => setFormData({ ...formData, nivel_combustivel: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <TextField
            label="Observações Gerais"
            multiline
            rows={3}
            value={formData.observacoes}
            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            fullWidth
            startIcon={<Add />}
            onClick={criarVistoria}
            disabled={criando}
            size="small"
          >
            {criando ? 'Criando...' : 'Iniciar'}
          </Button>
        </Paper>
      ) : (
        /* VISTORIA ATIVA */
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <h4 style={{ margin: 0, fontSize: '16px' }}>#{vistoria.id} - {tipoVistoria === 'entrega' ? '📍 Entrega' : '🔙 Devolução'}</h4>
              <Chip
                label={vistoria.status === 'em_andamento' ? 'Andando' : 'Concluída'}
                size="small"
                color={vistoria.status === 'em_andamento' ? 'warning' : 'success'}
              />
            </Box>

            <p style={{ margin: '8px 0', fontSize: '13px' }}>
              <strong>KM:</strong> {vistoria.km_final} | <strong>Combustível:</strong> {vistoria.nivel_combustivel}%
            </p>
          </Paper>

          {/* FOTOS */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Fotos ({fotos.length})</h4>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              {fotos.map(foto => (
                <Grid item xs={6} key={foto.id}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="120"
                      image={foto.url}
                      onClick={() => window.open(foto.url, '_blank')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', p: 0.5, fontSize: '11px' }}>
                      <div>{foto.tipo_foto}</div>
                      {foto.descricao && <div style={{ fontSize: '10px', marginTop: '2px' }}>{foto.descricao.substring(0, 20)}...</div>}
                    </Box>
                    {vistoria.status === 'em_andamento' && (
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'rgba(255,0,0,0.8)' }}
                        onClick={() => deletarFoto(foto.id)}
                      >
                        <DeleteIcon sx={{ fontSize: '16px', color: 'white' }} />
                      </IconButton>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>

            {vistoria.status === 'em_andamento' && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PhotoCamera />}
                    onClick={() => setCameraAtiva(true)}
                    fullWidth
                  >
                    Câmera
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUpload />}
                    onClick={() => fileInputRef.current?.click()}
                    fullWidth
                  >
                    Upload
                  </Button>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      for (let i = 0; i < files.length; i++) {
                        setFotoAtual(files[i]);
                        setDialogFotoOpen(true);
                        break;
                      }
                    }
                  }}
                />
              </>
            )}
          </Paper>

          {vistoria.status === 'em_andamento' && (
            <Button
              variant="contained"
              fullWidth
              color="success"
              onClick={finalizarVistoria}
              size="small"
            >
              Finalizar
            </Button>
          )}
        </>
      )}

      {/* CÂMERA MODAL */}
      <Dialog open={cameraAtiva} maxWidth="sm" fullWidth>
        <DialogTitle>Câmera</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: '100%', borderRadius: '8px' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCameraAtiva(false)}>Cancelar</Button>
          <Button onClick={capturarFoto} variant="contained">Capturar</Button>
        </DialogActions>
      </Dialog>

      {/* FOTO UPLOAD MODAL - OBSERVAÇÕES */}
      <Dialog open={dialogFotoOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Foto</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fotoAtual && (
            <img
              src={URL.createObjectURL(fotoAtual)}
              alt="Preview"
              style={{ width: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'cover' }}
            />
          )}

          <TextField
            label="Tipo de Foto"
            select
            value={tipoFotoAtual}
            onChange={(e) => setTipoFotoAtual(e.target.value)}
            size="small"
            disabled={uploadAndando}
            SelectProps={{
              native: true
            }}
          >
            <option value="geral">Geral</option>
            <option value="frontal">Frontal</option>
            <option value="traseira">Traseira</option>
            <option value="lateral_esq">Lateral Esquerda</option>
            <option value="lateral_dir">Lateral Direita</option>
            <option value="interior">Interior</option>
            <option value="dano">Dano</option>
            <option value="km">Km</option>
            <option value="combustivel">Combustível</option>
          </TextField>

          <TextField
            label="Observações da Foto"
            multiline
            rows={3}
            placeholder="Ex: Riscos na porta, vidro trincado, etc"
            value={obsAtualFoto}
            onChange={(e) => setObsAtualFoto(e.target.value)}
            size="small"
            disabled={uploadAndando}
          />

          {uploadAndando && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: '12px' }}>
                <span>Enviando...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogFotoOpen(false); setFotoAtual(null); }} disabled={uploadAndando}>Cancelar</Button>
          <Button onClick={salvarFoto} variant="contained" disabled={uploadAndando}>
            {uploadAndando ? `Enviando ${Math.round(uploadProgress)}%` : 'Salvar Foto'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
